import { Method } from "@/typescript/Services/request";
import { Snackbar } from "@varlet/ui";
import HttpResponse from "./HttpResponse";
import Global from "./Global";
import { ColorEnum, ResultCodeEnum } from "./SuperEnum";
import { TradeResult } from "@/types/general";
import { TimeSync } from "./TimeSync";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'; // 必须先导入 utc 插件
import timezone from 'dayjs/plugin/timezone'; // 导入 timezone 插件

// 加载插件
dayjs.extend(utc);
dayjs.extend(timezone);

export default class Tools {
    /**
   * 格式化毫秒数
   * @param milliseconds
   */
    public static FormatTimeWithMilliseconds(milliseconds: number, format: "HH:mm:ss.fff||mm:ss.fff" | "HH:mm:ss||mm:ss" | "HH:mm:ss.fff" | "HH:mm:ss" | "HH:mm" | "HHmmss" | "mm:ss" | "mmss" | "ss" | "fff") {
        // 计算小时
        let hours: number | string = (Math.floor(milliseconds / (3600 * 1000)));
        // 计算分钟
        let minutes: number | string = Math.floor((milliseconds % (3600 * 1000)) / (60 * 1000));
        // 计算剩余的秒数
        let seconds: number | string = Math.floor((milliseconds % (60 * 1000)) / 1000);
        //计算毫秒数
        let remainingMilliseconds: number | string = Math.floor(milliseconds % 1000);

        const str_hours = hours.toString().padStart(2, '0');
        const str_minutes = minutes.toString().padStart(2, '0');
        const str_seconds = seconds.toString().padStart(2, '0');
        const str_remainingMilliseconds = remainingMilliseconds.toString().padStart(3, '0');

        switch (format) {
            case "HH:mm:ss.fff||mm:ss.fff":
                if (hours > 0) {
                    return `${str_hours}:${str_minutes}:${str_seconds},${str_remainingMilliseconds}`;
                }
                else {
                    return `${str_minutes}:${str_seconds}.${str_remainingMilliseconds}`;
                }
            case "HH:mm:ss||mm:ss":
                if (hours > 0) {
                    return `${str_hours}:${str_minutes}:${str_seconds}`;
                }
                else {
                    return `${str_minutes}:${str_seconds}`;
                }
            case "HH:mm:ss.fff":
                return `${str_hours}:${str_minutes}:${str_seconds}.${str_remainingMilliseconds}`;
            case "HH:mm:ss":
                return `${str_hours}:${str_minutes}:${str_seconds}`;
            case "HH:mm":
                return `${str_hours}:${str_minutes}`;
            case "HHmmss":
                return `${str_hours}${str_minutes}${str_seconds}`;
            case "mm:ss":
                return `${str_minutes}:${str_seconds}`;
            case "mmss":
                return `${str_minutes}${str_seconds}`;
            case "ss":
                return `${str_seconds}`;
            case "fff":
                return `${str_remainingMilliseconds}`;
        }
    }

    /**
     * 获取当前时间戳
     */
    public static get GetNowTime(): number {
        return TimeSync.getServerTime();
    }

    /**
     * 时间戳转换为美国指定时区的时间
     * @param {number} timestamp - 时间戳（秒级/毫秒级）
     * @param {string} timezone - 美国时区 ID（如 'America/New_York'）
     * @param {string} formatStr - 自定义格式（如 'YYYY-MM-DD HH:mm:ss'）
     * @returns {string} 格式化后的时间字符串
     */
    public static ConvertTimestamp(timestamp: number,
        timezone: 'America/New_York' = 'America/New_York',
        formatStr: 'YYYY-MM-DD HH:mm:ss' | 'MM-DD HH:mm:ss' | 'MM-DD HH:mm' | 'YYYY-MM-DD' | 'HH:mm:ss' = 'YYYY-MM-DD HH:mm:ss') {
        if (Tools.IsNull(timezone))
            timezone = "America/New_York";
        if (Tools.IsNull(formatStr))
            formatStr = "YYYY-MM-DD HH:mm:ss";
        // 处理秒级时间戳
        const msTimestamp = timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;
        // 使用 timezone 插件转换时区
        return dayjs(msTimestamp).tz(timezone).format(formatStr);
    }

    /**
     * 弹出成功提示
     * @param msg
     */
    public static AlertSuccess(msg: string) {
        Snackbar({
            content: msg,
            type: "success",
            position: "top"
        });
    }

    /**
     * 弹出失败提示
     * @param msg
     */
    public static AlertError(msg: string) {
        Snackbar({
            content: msg,
            type: "error",
            position: "top"
        });
    }

    /**
     * 检测是否为空
     * @param v
     * @returns
     */
    public static IsNull(v: (string | undefined) | (string | null)): boolean {
        if (v == null || v == "null" || v == undefined || v == "undefined" || v == "")
            return true;
        return false;
    }

    /**
     * 是否不是空
     * @param v
     * @returns
     */
    public static IsNotNull(v: (string | undefined) | (string | null)): boolean {
        return !this.IsNull(v);
    }

    /**
     * 检查字符串能否转换为数字
     * @param value 
     * @returns 
     */
    public static IsNumeric(value: any) {
        return !isNaN(value) && !isNaN(parseFloat(value));
    }

    /**
   * 获取网络文件
   * @param url 请求地址
   * @param method 模式
   * @param body 数据
   * @param [checkTimeOut=false] 是否检查超时
   */
    public static async FetchGetFileAsync(url: string, body: any, method: "POST" | "GET" | "DELETE" | "PUT" | "PATCH",
        ContentType: 'application/json' | 'text/plain' | 'multipart/form-data' | 'application/x-www-form-urlencoded' = 'application/json',
        Headers: { [key: string]: any }
    ): Promise<Blob> {
        switch (method) {
            case "GET":
            case "DELETE":
                break;
            default:
                break;
        }

        let fetchBody: BodyInit | undefined
        switch (method) {
            case "GET":
            case "DELETE":
                delete Headers['Content-Type'];
                if (body != null && Object.keys(body).length > 0)
                    url += '?';
                for (const key in body) {
                    url += `${key}=${encodeURIComponent(body[key])}&`;
                }
                if (url.endsWith('&'))
                    url = url.substring(0, url.length - 1);
                break;
            default:
                switch (ContentType) {
                    case "application/json":
                        fetchBody = JSON.stringify(body);
                        break;
                    case "application/x-www-form-urlencoded":
                        const urlSearchParams = new URLSearchParams();
                        Object.keys(body).forEach(key => {
                            urlSearchParams.append(key, String(body[key]));
                        });
                        fetchBody = urlSearchParams.toString();
                        break;
                    case "multipart/form-data":
                        delete Headers['Content-Type'];
                        const formData = new FormData();
                        Object.keys(body).forEach(key => {
                            formData.append(key, body[key]);
                        });
                        fetchBody = formData;
                        break;
                    case "text/plain":
                        fetchBody = typeof body === "string" ? body : JSON.stringify(body);
                        break;
                }
                break;
        }
        return new Promise(r => {
            globalThis.fetch(url, {
                headers: Headers,
                method: method,
                body: fetchBody
            }).then(resp => {

                //#region 经过类型处理

                //#endregion

                return resp.blob();
            }).then(v => {
                r(v);
            });
        });
    }


    /**
     * Get请求
     * @param action
     * @param getContent
     * @returns
     */
    public static FetchGetAsync<T = any>(action: string, getContent: any) {
        return this.FetchAsync<T>(action, getContent, "GET", "text/plain");
    }



    /**
     * 进行网络请求
     * @param action 具体指令
     * @param method 模式
     * @param body 数据
     * @param verifyJwtToken 是否验证登录Token
     * @param [checkTimeOut=false] 是否检查超时
     */
    public static async FetchAsync<T = any>(action: string, body: any, method: Method,
        ContentType: 'application/json' | 'text/plain' | 'multipart/form-data' | 'application/x-www-form-urlencoded' = 'application/json',
        verifyJwtToken: boolean = true
    ): Promise<HttpResponse<T>> {
        let url = Global.GetAPIUrl(action);

        //#region 验证JWT是否有效
        if (verifyJwtToken) {
            if (!Global.JWTTokenEffective()) {
                //执行刷新Token
                if (Tools.IsNotNull(Global.RefreshToken)) {
                    if (!await Global.RefreshTokenAsync()) {
                        //刷新Token失败,则尝试登录
                        await Global.TryToLoginAsync();
                    }
                }
                else {
                    await Global.TryToLoginAsync();
                }
            }
        }
        //#endregion

        const headers: HeadersInit = {
            "Authorization": `Bearer ${Global.JwtToken}`,
            "Content-Type": ContentType
        };
        switch (method) {
            case "GET":
            case "get":
            case "delete":
            case "DELETE":
                break;
            default:
                break;
        }

        for (const key in body) {
            if (Object.prototype.hasOwnProperty.call(body, key)) {
                const v = body[key];
                if (v == null || v == undefined) {
                    delete body[key];
                }
            }
        }


        let fetchBody: BodyInit | undefined
        switch (method) {
            case "GET":
            case "get":
            case "delete":
            case "DELETE":
                delete headers['Content-Type'];
                if (body != null && Object.keys(body).length > 0)
                    url += '?';
                for (const key in body) {
                    url += `${key}=${encodeURIComponent(body[key])}&`;
                }
                if (url.endsWith('&'))
                    url = url.substring(0, url.length - 1);
                break;
            default:
                switch (ContentType) {
                    case "application/json":
                        fetchBody = JSON.stringify(body);
                        break;
                    case "application/x-www-form-urlencoded":
                        const urlSearchParams = new URLSearchParams();
                        Object.keys(body).forEach(key => {
                            urlSearchParams.append(key, String(body[key]));
                        });
                        fetchBody = urlSearchParams.toString();
                        break;
                    case "multipart/form-data":
                        delete headers['Content-Type'];
                        const formData = new FormData();
                        Object.keys(body).forEach(key => {
                            const v = body[key];
                            if (typeof v == "object") {
                                if ('length' in v) {
                                    for (let i = 0; i < v.length; i++) {
                                        formData.append(key, v[i]);
                                    }
                                }
                                else {
                                    formData.append(key, v);
                                }
                            }
                            else {
                                formData.append(key, v);
                            }
                        });
                        fetchBody = formData;
                        break;
                    case "text/plain":
                        fetchBody = typeof body === "string" ? body : JSON.stringify(body);
                        break;
                }
                break;
        }
        return new Promise(r => {
            globalThis.fetch(url, {
                headers: headers,
                method: method,
                body: fetchBody
            }).then(async response => {

                const statusCode = response.status;
                let resp = new HttpResponse<T>();
                switch (statusCode) {
                    case 400:
                        resp.code = ResultCodeEnum.Error;
                        resp.msg = "Other Errors!";
                        return JSON.stringify(resp);
                    case 500:
                        resp.code = ResultCodeEnum.Error;
                        resp.msg = "Server Error!";
                        return JSON.stringify(resp);
                    case 401: //未登录
                        resp.code = ResultCodeEnum.Error;
                        resp.msg = "";
                        // 检查当前路由是否为首页，如果是首页则不显示登录弹窗
                        import('@/router').then(router => {
                            const currentRoute = router.default.currentRoute.value;
                            switch (currentRoute.path) {
                                case '/':
                                case "/about":
                                    break;
                                default:
                                    Global.ShowLogin();
                                    break;
                            }
                        });
                        return JSON.stringify(resp);
                    case 403: //拒绝访问
                        resp.code = ResultCodeEnum.Error;
                        resp.msg = "Access Denied!";
                        return JSON.stringify(resp);
                }

                //#region 进行类型处理
                const contentType = response.headers.get("Content-Type");
                if (contentType) {
                    if (contentType.includes('application/json')) {
                        // 处理 JSON 数据
                        return response.json();
                    } else if (contentType.includes('text/')) {
                        // 处理文本数据
                        return response.text();
                    } else if (contentType.includes('image/')) {
                        // 处理图片数据
                        return response.blob();
                    } else if (contentType.includes('audio/') || contentType.includes('video/')) {
                        // 处理音频或视频数据
                        return response.blob();;
                    }
                    else if (contentType.includes('application/problem+json')) {
                        const parsedBody = await response.json();
                        throw new Error(`[${response.status}] ${JSON.stringify(parsedBody.errors || {}, null, 2)}`);
                    }
                    else {
                        // 处理其他类型的数据，默认作为 Blob 处理
                        return response.blob();
                    }
                }
                else {
                    // 没有 Content-Type 的情况
                    const blobData = response.blob();
                    return { type: 'unknown', data: blobData };
                }
                //#endregion
            }).then(v => {
                if (!v) {
                    throw new Error("No Data Returned");
                }
                if (v instanceof Blob) {
                    const nResp = new HttpResponse<T>();
                    nResp.code = ResultCodeEnum.Success;
                    nResp.msg = "";
                    nResp.data = v as any as T;
                    r(nResp);
                    return;
                }
                else if (typeof v == "string") {
                    v = JSON.parse(v);
                }
                Object.setPrototypeOf(v, HttpResponse.prototype);
                r(v);
            }).catch(err => {
                console.error('❌ Exception Caught:', err.message);
                const errorResp = new HttpResponse<T>();
                errorResp.code = ResultCodeEnum.Error;
                errorResp.msg = err.message;
                errorResp.data = null as any;
                r(errorResp);
            });
        });
    }

    /**
     * 生成随机数
     * @param min 最小数(包含)
     * @param max 最大数(包含)
     * @returns 
     */
    public static RandNumber(min: number, max: number) {
        min = Math.ceil(min);   // 向上取整，确保是整数
        max = Math.floor(max);  // 向下取整，确保是整数
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    /**
   * 解析 JWT
   * @param {string} token - JWT 字符串
   * @returns {{ header: Object, payload: Object, signature: string }} 解析后的 JWT 部分
   */
    public static ParseJWT(token: string): {
        header: {
            alg: string,
            typ: string
        },
        payload: {
            iss: string,
            "http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata": string,
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier": string,
            "exp": number,
            "aud": string,
            UserInfo: DB.User
        },
        signature: string
    } {
        if (!token) {
            throw new Error("JWT Token Error!");
        }

        const parts = token.split('.');

        if (parts.length !== 3) {
            throw new Error("Invalid JWT Token");
        }

        const [encodedHeader, encodedPayload, signature] = parts;

        // 解码 Header 和 Payload
        const decodedHeader = this.Base64UrlDecode(encodedHeader);
        const decodedPayload = this.Base64UrlDecode(encodedPayload);

        // 解析 JSON
        const header = JSON.parse(decodedHeader);
        const payload = JSON.parse(decodedPayload);
        const userJson = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata'];
        if (typeof userJson == 'string')
            payload.UserInfo = JSON.parse(userJson);
        return {
            header,
            payload,
            signature
        };
    }
    /**
     * Base64 解码
     * @param input 
     * @returns 
     */
    private static Base64UrlDecode(input: string): string {
        // 替换 URL 特殊字符
        input = input.replace(/-/g, '+').replace(/_/g, '/');
        // 添加必要的填充符号
        const pad = input.length % 4;
        if (pad) {
            input += '='.repeat(4 - pad);
        }
        // 使用 atob 解码为二进制字符串
        const binary = atob(input);
        // 将二进制字符串转换为字节数组
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        // 使用 TextDecoder 解码为 UTF-8 字符串
        const decoder = new TextDecoder('utf-8');
        return decoder.decode(bytes);
    }

    /**
     * 显示LOG
     * @param tag 标签
     * @param msg 日志内容
     * @param color 颜色
     * @param font_size 字体大小
    */
    public static ShowLog(tag: string, msg: string, color: ColorEnum = ColorEnum.Info, font_size: number = 12): void {
        console.log(`%c【${tag}】:${msg}`, `color:${color};font-size:${font_size}px`);
    }

    /**
     * 数组随机
     * @param array 
     * @returns 
     */
    public static ShuffleArray<T>(array: T[]) {
        const result = array.slice(); // 复制数组，避免修改原数组
        for (let i = result.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1)); // 随机索引
            [result[i], result[j]] = [result[j], result[i]]; // 交换元素
        }
        return result;
    }

    /**
   * 获取本地存储
   * @param key
   * @returns
   */
    public static GetLocalStorage(key: string, _default: string): string {
        let v = localStorage.getItem(key);
        if (Tools.IsNull(key))
            return "";
        return v as string;
    }

    /**
    * 设置本地存储
    * @param key
    * @param v
    */
    public static SetLocalStorage(key: string, v: string): void {
        localStorage.setItem(key, v);
    }

    /**
     * 删除键
     * @param key
     */
    public static RemoveLocalStorage(key: string): void {
        localStorage.removeItem(key);
    }

    /**
     * 清空存储键
     */
    public static ClearLocalStorage() {
        localStorage.clear();
    }

    /**
     * 保留指定小数位，但不四舍五入
     * @param num 
     * @param digits 
     * @returns 
     */
    public static TruncateDecimal(num: number, digits = 2) {
        const numStr = num.toString();
        const decimalIndex = numStr.indexOf('.');

        if (decimalIndex === -1) {
            return num; // 整数直接返回
        }

        return parseFloat(numStr.substring(0, decimalIndex + digits + 1));
    }

    /**
     * 补零到指定小数位数
     * @param num 原始数字
     * @param digits 要保留的小数位数（不足补0）
     * @returns 字符串形式的数字，确保小数位长度符合要求
     */
    public static PadDecimal(num: number, digits = 2): string {
        if (isNaN(+num))
            num = 0;
        let [integerPart, decimalPart = ""] = num.toString().split(".");

        // 截取或补0
        decimalPart = decimalPart.substring(0, digits).padEnd(digits, "0");

        return `${integerPart}.${decimalPart}`;
    }

    /**
     * 计算涨幅比
     * @param day1Amount 
     * @param day0Amount 
     */
    public static IncreaseRatio(day1Amount: number, day0Amount: number) {
        if (day1Amount == 0 || day0Amount == 0)
            return 0;
        let bfb = (day1Amount - day0Amount) / day1Amount * 100;
        return Tools.TruncateDecimal(bfb);
    }

    /**
     * 计算赢收
     * @param pOpen 开盘价
     * @param pClose 锁仓价
     * @param q 手数量
     * @param leverage 杠杆
     * @param contractSize 每手的合约面值
     * @returns 
     */
    public static CalculatePnL(
        pOpen: number,
        pClose: number,
        q: number,
        leverage: number,
        contractSize: number = 1
    ): TradeResult {
        // 盈亏金额
        const pnl = (pClose - pOpen) * q * contractSize;
        // 保证金
        const margin = (pOpen * q * contractSize) / leverage;
        // 收益率
        const roi = margin !== 0 ? pnl / margin : 0;
        return { pnl, margin, roi };
    }

    /**
     * 下载图片
     * @param source 
     * @param filename 
     * @returns 
     */
    public async DownloadImageAsync(
        source: string | HTMLCanvasElement | Blob,
        filename = 'image.png'
    ) {
        let url = '';

        // canvas
        if (source instanceof HTMLCanvasElement) {
            url = source.toDataURL('image/png');
        }
        // blob
        else if (source instanceof Blob) {
            url = URL.createObjectURL(source);
        }
        // base64 或 url 字符串
        else if (typeof source === 'string') {
            if (source.startsWith('data:image')) {
                url = source;
            } else if (/^https?:\/\//.test(source)) {
                // 处理跨域图片下载
                try {
                    const res = await fetch(source, { mode: 'cors' });
                    const blob = await res.blob();
                    url = URL.createObjectURL(blob);
                } catch (e) {
                    console.error('Image Download Failure:', e);
                    return;
                }
            } else {
                console.error('Unsupported Image Source Format');
                return;
            }
        }

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();

        // 清理 URL 对象
        if (source instanceof Blob || /^https?:\/\//.test(source as string)) {
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
    }

}

